// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/MetaAdsCard.tsx
// ADAPTED (plan K0/K2, G8-G10): TP's job card (header, badge, source line, growing log, ad thumbnails), driven only by
// the ad feed and the ad analysis. TP's scripted say lines, ad scores, Creative Health ticks and funnel cards have no
// source here and are not ported; the rows with live data (days running, media, landing page) are onboarding's.
import { AlertCircle, Bot, ExternalLink } from 'lucide-react';
import { useStickToBottom } from '@/components/rooms/useStickToBottom';
import { Card } from '@/components/ui/shadcn/card';
import type { AdAnalysisSummary, AdPlatform, NeutralAd, StepState } from '@/contracts/onboarding-session';
import { safeHttpUrl } from '@/lib/safeHttpUrl';
import { cn } from '@/lib/utils';

import type { AdDiscoveryState } from '../ads/adDiscovery.reducer';
import { ADS_SHOWN } from '../constants';
import { ADS_CARD, ADS_CARD_CAPTION, countLabel, humanizeToken } from '../copy';
import { channelName } from '../quiz/questions';
import type { DataState } from '../state/buildTranscript';
import { isTerminalState } from '../state/jobState';
import { AdAnalysisBlock } from './AdAnalysis';
import { AdThumb } from './AdThumb';
import { JobBadge } from './JobBadge';
import { STEP_STATUS_TEXT, StepStatusIcon } from './StepStatusIcon';

/** The stagger between the first ads of a batch (TP's `i * 60ms`). */
const STAGGER_MS = 60;

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Most days running first; an ad with no days running goes last. Equal days keep the order they arrived in. */
export function rankByDaysRunning(ads: readonly NeutralAd[]): NeutralAd[] {
  return ads
    .map((ad, index) => ({ ad, index }))
    .sort((a, b) => (b.ad.daysRunning ?? -1) - (a.ad.daysRunning ?? -1) || a.index - b.index)
    .map(({ ad }) => ad);
}

/* ---------------------------------- badge ---------------------------------- */

export type AdsBadgeStatus = 'queued' | 'running' | 'done' | 'partial' | 'unavailable';

/**
 * The discovery's status: the feed's, except that a feed with no event yet reads as the job's own end when the
 * snapshot says the job already ended (a resumed session, a refresh): nothing is searching then, whatever the feed
 * has not replayed. A job that was skipped found nothing to show, which reads as a finished (empty) search.
 */
export function discoveryStatus(feedStatus: AdDiscoveryState['status'], jobState?: StepState): AdDiscoveryState['status'] {
  if (feedStatus !== 'idle' || jobState === undefined || !isTerminalState(jobState)) return feedStatus;
  return jobState === 'skipped' ? 'done' : jobState;
}

/**
 * The card's one status word, from what the feed and the analysis report (never a timer):
 * `unavailable` when the library could not be read; `queued` before the first event of a job still queued;
 * `running` while the search or the analysis works; else `partial` if either ended short, else `done`.
 */
export function adsBadgeStatus(
  feedStatus: AdDiscoveryState['status'],
  analysis: DataState<AdAnalysisSummary> | undefined,
  hasAds: boolean,
  jobState?: StepState,
): AdsBadgeStatus {
  const discovery = discoveryStatus(feedStatus, jobState);
  if (discovery === 'unavailable') return 'unavailable';
  if (discovery === 'idle') return jobState === 'queued' ? 'queued' : 'running';
  if (discovery === 'running') return 'running';
  // The search is over. With ads to look at, the analysis is the rest of the job.
  let analysisPartial = false;
  if (analysis && hasAds) {
    if (analysis.state === 'idle' || analysis.state === 'loading') return 'running';
    if (analysis.state === 'error') analysisPartial = true;
    else if (analysis.data.state === 'running' || analysis.data.state === 'queued') return 'running';
    else analysisPartial = analysis.data.state === 'partial' || analysis.data.state === 'unavailable';
  }
  return discovery === 'partial' || analysisPartial ? 'partial' : 'done';
}

/* ----------------------------------- rows ---------------------------------- */

function AdRow({ ad, index }: { ad: NeutralAd; index: number }) {
  const landing = safeHttpUrl(ad.landingUrl);
  const name = ad.advertiser.name ?? ADS_CARD.unnamedPage;
  const headline = ad.title?.trim() || ad.copy?.trim() || ADS_CARD.untitledAd;
  const media = ad.mediaType?.trim() ? humanizeToken(ad.mediaType) : null;
  return (
    <li
      data-testid="ad-row"
      className="flex items-center gap-3 border-t border-ds-hairline py-2 animate-in fade-in duration-300 fill-mode-backwards first:border-t-0 motion-reduce:animate-none"
      style={{ animationDelay: `${index * STAGGER_MS}ms` }}
    >
      <span className="relative shrink-0">
        <AdThumb src={ad.thumbnailUrl} />
        {ad.daysRunning !== undefined ? (
          <span aria-hidden className="absolute left-1 top-1 rounded-xs bg-ds-ink px-1 text-caption text-ds-paper">
            {`${ad.daysRunning}d`}
          </span>
        ) : null}
      </span>
      <div className="min-w-0 flex-1">
        <p data-testid="ad-headline" className="truncate text-body-sm text-foreground">{headline}</p>
        <p className="truncate text-label text-ds-text-2">{name}</p>
        <p className="flex gap-1.5 truncate text-label text-ds-text-3">
          {media ? <span data-testid="ad-media">{media}</span> : null}
          {media ? <span aria-hidden="true">·</span> : null}
          <span>
            {ad.daysRunning !== undefined ? `Running ${countLabel(ad.daysRunning, 'day', 'days')}` : ADS_CARD.runningUnknown}
          </span>
        </p>
        {landing ? (
          <a
            href={landing}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-1 truncate text-label text-ds-text-2 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
          >
            <span className="truncate">{hostOf(landing)}</span>
            <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        ) : null}
      </div>
    </li>
  );
}

const ENTRANCE = 'animate-in fade-in duration-300 motion-reduce:animate-none';

export interface AdsCardProps {
  platform: AdPlatform;
  state: AdDiscoveryState;
  /** The brand ad profile of the target account; absent until it is wired, and the block is then left out. */
  analysis?: DataState<AdAnalysisSummary>;
  /** The brand's name, for the title ("Reading Truekind's live Meta ads"). */
  brand?: string | null;
  /** The site the ads link to, for the source line. */
  site?: string;
  /** The snapshot's state of this platform's discovery job: tells a job still `queued` from one that is searching. */
  jobState?: StepState;
}

/**
 * Scout's ad library card on one platform: TP's job card whose log grows in place. Under the source line the
 * search is a step (spinner while it runs); when it ends the log says how many ads it found and how many
 * duplicates it filtered; the ads follow, most days running first, with Scout's "Ad analysis" under them.
 * The log follows its newest item only while the reader is near its bottom. Platform-neutral.
 */
export function AdsCard({ platform, state, analysis, brand, site, jobState }: AdsCardProps) {
  const label = channelName(platform);
  const ads = rankByDaysRunning(state.order.map((id) => state.ads[id]!).filter(Boolean));
  const status = discoveryStatus(state.status, jobState);
  const searching = status === 'idle' || status === 'running';
  const duplicates = state.terminal?.duplicateCreatives ?? 0;
  const warnings = [...new Set([...state.warnings, ...(state.terminal?.warningCodes ?? [])])];
  const badgeKey = adsBadgeStatus(state.status, analysis, ads.length > 0, jobState);
  const searchStep: StepState = status === 'idle' || status === 'running' ? 'running' : status;
  const who = brand?.trim() || site;

  // The log follows its newest item, but never drags a reader who scrolled up (TP always scrolls). The same hook as
  // the chat thread, so both follow and let go of the bottom by the same rules.
  const growth = [
    ads.length,
    state.status,
    warnings.length,
    analysis?.state === 'ready' ? `${analysis.data.state}:${analysis.data.coverage.analyzed}` : (analysis?.state ?? ''),
  ].join('|');
  const { listRef: logRef, scrollerProps } = useStickToBottom<HTMLDivElement>(growth);

  return (
    <Card
      role="region"
      aria-label={`${label} ads`}
      data-testid="ads-card"
      data-state={badgeKey}
      className="w-full overflow-hidden"
    >
      <header className="flex items-center gap-2.5 border-b border-ds-hairline px-3 py-2.5">
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2">
          <Bot className="size-3.5" />
        </span>
        <h3 className="min-w-0 flex-1 truncate text-heading-sm text-foreground">
          {who ? `Reading ${who}'s live ${label} ads` : `Reading your live ${label} ads`}
        </h3>
        <JobBadge status={badgeKey} data-testid="ads-badge" />
      </header>
      <div
        ref={logRef}
        {...scrollerProps}
        // A scrollable region is a keyboard stop (WCAG 2.1.1: its ads can be read without a pointer) and needs a name.
        role="group"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the focus stop IS the point: it scrolls
        tabIndex={0}
        aria-label={ADS_CARD.logLabel(label)}
        data-testid="ads-log"
        className="flex max-h-[420px] flex-col gap-2 overflow-y-auto px-3 py-3"
      >
        <p className="text-label text-ds-text-3">{`Source: ${label} Ad Library · ads linking to ${site ?? 'your site'}`}</p>

        <div className={cn('flex items-center gap-2.5', ENTRANCE, 'slide-in-from-bottom-1')} data-status={searchStep}>
          <StepStatusIcon status={searchStep} />
          <span className={cn('min-w-0 flex-1 truncate text-body-sm', searchStep === 'running' ? 'onb-shimmer' : 'text-foreground')}>
            {`Searching the ${label} ad library`}
          </span>
          <span className="sr-only">{`(${STEP_STATUS_TEXT[searchStep]})`}</span>
        </div>

        {status === 'unavailable' ? (
          <p className={cn('text-body-sm text-ds-text-2', ENTRANCE)}>{ADS_CARD.unavailable(label)}</p>
        ) : null}
        {!searching && status !== 'unavailable' ? (
          <p className={cn('text-body-sm text-ds-text-2', ENTRANCE)}>
            {`Found ${countLabel(ads.length, 'ad', 'ads')}.`}
            {duplicates > 0 ? (
              <span data-testid="ads-duplicates">{` ${countLabel(duplicates, 'duplicate', 'duplicates')} filtered.`}</span>
            ) : null}
          </p>
        ) : null}
        {warnings.map((code) => (
          <p key={code} data-testid="ads-warning" className={cn('flex items-start gap-1.5 text-body-sm text-ds-text-2', ENTRANCE)}>
            <AlertCircle aria-hidden className="mt-0.5 size-3.5 shrink-0 text-ds-badge-warning-text" />
            {ADS_CARD.warning(humanizeToken(code).toLowerCase())}
          </p>
        ))}

        {ads.length > 0 ? (
          <div className="flex flex-col">
            <p data-testid="ads-live-count" className="text-label text-ds-text-2">
              {countLabel(ads.length, 'live ad', 'live ads')}
            </p>
            <ul>
              {ads.slice(0, ADS_SHOWN).map((ad, i) => (
                <AdRow key={ad.adId} ad={ad} index={i} />
              ))}
            </ul>
            {ads.length > ADS_SHOWN ? (
              <p className="border-t border-ds-hairline pt-1.5 text-label text-ds-text-3">{`+ ${ads.length - ADS_SHOWN} more`}</p>
            ) : null}
            {!searching ? <p className="pt-1.5 text-label text-ds-text-3">{ADS_CARD_CAPTION}</p> : null}
          </div>
        ) : null}

        {analysis ? <AdAnalysisBlock analysis={analysis} /> : null}
      </div>
    </Card>
  );
}
